import styled from '@emotion/styled';
import { Avatar, Box, Divider, Menu, Text } from '@mantine/core';
import SupportIcon from '@material-icons/svg/svg/support_agent/outline.svg';
import ArticleIcon from '@material-icons/svg/svg/article/outline.svg';
import PolicyIcon from '@material-icons/svg/svg/policy/outline.svg';
import LogoutIcon from '@material-icons/svg/svg/logout/outline.svg';
import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { useIsMobileMedia, useIsTabletOrMobileMedia, useUser, useUserProfile } from '~/hooks';
import { getFirebaseAuth } from '~/lib/firebase';
import { useLogoutMutation } from '~/redux/slices/userSlice';
import { color, fontSize, getColor, mediaQueryMobile, mediaQueryTablet } from '~/styles/mixins';
import * as constants from '~/constants';
import { selectUnseenShortlistedCandidateCount } from '~/redux/slices/appSlice';

import ArrowDropdownIconComponent from '~/assets/icons/arrow_drop_down.svg';
import BrowseCandidatesIcon from '~/assets/icons/browse-candidates.svg';
import ShortlistedCandidatesIcon from '~/assets/icons/bx-user-check.svg';
import UpraisedLogoUrl from '~/assets/companyLogos/upraised.svg';

const Root = styled.nav`
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);
  background: #fff;
  transition: max-height 250ms ease, height 250ms ease;
  overflow: hidden;
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 2;
  height: 64px;
  max-height: 1000px;
  ${mediaQueryTablet} {
    height: auto;
    max-height: ${(props) => (props.isExpanded ? '1000px' : '64px')};
  }
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
  ${mediaQueryTablet} {
    flex-direction: column;
    align-items: unset;
    padding: 0 16px;
  }
`;

// Content > LogoLink
const LogoLink = styled(Link)`
  text-decoration: none;
  display: flex;
  align-items: center;
  height: 100%;
  padding: 16px 0;
  ${mediaQueryTablet} {
    height: 64px;
  }
`;

const UpraisedLogo = styled(UpraisedLogoUrl)`
  flex-shrink: 0;
  height: 100%;
`;

// Content > NavContent
const NavContent = styled(Box)`
  display: flex;
  flex: 1;
  align-items: center;
  height: 100%;
  padding: 0 32px;
  ${mediaQueryTablet} {
    flex-direction: column;
    align-items: flex-start;
    margin-top: 16px;
    padding: 16px 0;
  }
`;

const NavLinkItemLabel = styled(Text)`
  ${color('gray', 8)}
  ${fontSize('sm')}
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  svg {
    width: 24px;
    flex-shrink: 0;
  }
  ${mediaQueryMobile} {
    flex-direction: column;
  }
`;

const NavItemBadge = styled(Box)`
  width: 22px;
  height: 22px;
  font-size: 14px;
  border-radius: 100%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 11px;
  left: -14px;
  background: ${getColor('orange', 7)};
  color: #fff;
  ${mediaQueryMobile} {
    width: 16px;
    height: 16px;
    font-size: 12px;
    top: -4px;
    left: -8px;
  }
`;

// NavContent >  NavLinkItem
const NavLinkItem = styled(Box)`
  display: flex;
  align-items: center;
  text-decoration: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 200ms ease;
  height: 100%;
  position: relative;
  ::after {
    content: '';
    position: absolute;
    transition: background-color 200ms ease;
    background: transparent;
    height: 4px;
    width: calc(100% + 12px);
    left: -6px;
    bottom: 0;
  }
  & + & {
    margin-left: 48px;
  }

  &.active,
  :hover {
    ::after {
      background: ${getColor('blue', 8)};
    }
    > ${NavLinkItemLabel} {
      ${color('blue', 8)}
    }
  }

  ${mediaQueryTablet} {
    &.active::after {
      background: transparent;
    }
    & + & {
      margin: 0;
    }
  }
`;
NavLinkItem.defaultProps = { component: NavLink };

// NavContent > NavUserMenu
const NavUserMenu = styled(Box)`
  display: flex;
  align-items: center;
  margin-left: auto;
  ${mediaQueryTablet} {
    // margin-left: 0;
    // margin-top: 32px;
  }
`;

const UserContainer = styled(Box)`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  ${color('gray', 6)}
`;

const ExternalLink = styled.a`
  text-decoration: none;
  color: #0f0e0e;
`;

const ArrowDropdownIcon = styled(Box)`
  ${color('gray', 7)}
`;
ArrowDropdownIcon.defaultProps = { component: ArrowDropdownIconComponent };

const MenuItemText = styled(Text)`
  background: none;
  cursor: text;
  user-select: text;
  padding: 4px 12px;
  ${color('gray', 7)}
`;

const NavUser = React.forwardRef(({ ...rest }, ref) => {
  const user = useUser();
  const isMobileMedia = useIsMobileMedia();
  return user ? (
    <UserContainer ref={ref} {...rest}>
      <Avatar radius={'100%'} src={user.avatar?.url} size={isMobileMedia ? '24px' : '32px'} />
      <Text component="span" ml="8px" weight="500" sx={(theme) => ({ color: theme.colors.gray[7] })}>
        {user.firstName}
      </Text>
      <ArrowDropdownIcon width="24px" />
    </UserContainer>
  ) : null;
});

const Navbar = () => {
  const isMobileMedia = useIsMobileMedia();
  const isTabletOrMobileMedia = useIsTabletOrMobileMedia();
  const user = useUser();
  const companyRep = useUserProfile(constants.USER_PROFILE_TYPES.COMPANY_REP);
  const [logoutUser] = useLogoutMutation();
  const unseenShortlistedCandidatesCount = useSelector(selectUnseenShortlistedCandidateCount);

  const handleLogoutClick = async () => {
    const auth = getFirebaseAuth();
    auth.signOut();
    logoutUser();
  };

  let showNavItems = false;
  if (companyRep?.hasFinishedOnboarding) {
    showNavItems = true;
  }

  const NavUserMenuComponent = () => {
    return (
      <NavUserMenu>
        <Menu
          trigger="hover"
          placement="start"
          control={<NavUser />}
          closeOnItemClick={false}
          style={{ width: isMobileMedia ? '100%' : 'max-content' }}
        >
          <Menu.Label>Account</Menu.Label>
          <Menu.Item component={MenuItemText} style={{ fontWeight: 500 }}>
            {user.name}
          </Menu.Item>
          <Menu.Item component={MenuItemText}>{user.email}</Menu.Item>
          <Menu.Item component={MenuItemText}>{user.phone}</Menu.Item>
          <Menu.Label mt="md">Help</Menu.Label>
          <Menu.Item icon={<SupportIcon width="20px" />}>
            <ExternalLink href="mailto:hire@upraised.co">Contact Us</ExternalLink>
          </Menu.Item>
          <Menu.Label>Legal</Menu.Label>
          <Menu.Item
            icon={<ArticleIcon width="20px" />}
            component="a"
            target="_blank"
            href="https://www.upraised.co/terms"
          >
            Terms of Service
          </Menu.Item>
          <Menu.Item
            icon={<PolicyIcon width="20px" />}
            component="a"
            target="_blank"
            href="https://www.upraised.co/privacy-policy"
          >
            Privacy Policy
          </Menu.Item>
          <Divider />
          <Menu.Item color="red" icon={<LogoutIcon width="20px" fill="currentColor" />} onClick={handleLogoutClick}>
            Logout
          </Menu.Item>
        </Menu>
      </NavUserMenu>
    );
  };

  return (
    <>
      <Root isExpanded={false}>
        <Content>
          <Box style={{ display: 'flex', alignItems: 'center', height: isTabletOrMobileMedia ? 'unset' : '100%' }}>
            <LogoLink to="/">
              <UpraisedLogo />
            </LogoLink>
            {isTabletOrMobileMedia ? <NavUserMenuComponent /> : null}
          </Box>

          <NavContent ml={isTabletOrMobileMedia ? undefined : '100px'}>
            {showNavItems ? (
              <>
                <NavLinkItem to={`/candidates/browse/`}>
                  <NavLinkItemLabel>
                    <BrowseCandidatesIcon />
                    Browse Candidates
                  </NavLinkItemLabel>
                </NavLinkItem>
                <NavLinkItem to={`/candidates/shortlisted/`}>
                  <NavLinkItemLabel>
                    {unseenShortlistedCandidatesCount > 0 ? (
                      <NavItemBadge>{unseenShortlistedCandidatesCount}</NavItemBadge>
                    ) : null}
                    <ShortlistedCandidatesIcon /> Shortlisted Candidates
                  </NavLinkItemLabel>
                </NavLinkItem>
              </>
            ) : null}
            <NavUserMenuComponent />
          </NavContent>
        </Content>
      </Root>
    </>
  );
};

export default Navbar;

const BottomNavbarRoot = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 0 16px;
  position: sticky;
  bottom: 0;
  z-index: 1;
  background: #fff;
  height: 64px;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.2);
`;

export const BottomNavbar = ({ ...rest }) => {
  const unseenShortlistedCandidatesCount = useSelector(selectUnseenShortlistedCandidateCount);

  return (
    <BottomNavbarRoot {...rest}>
      <NavLinkItem to={`/candidates/browse/`}>
        <NavLinkItemLabel>
          <BrowseCandidatesIcon />
          Browse Candidates
        </NavLinkItemLabel>
      </NavLinkItem>
      <NavLinkItem to={`/candidates/shortlisted/`}>
        <NavLinkItemLabel>
          <Box style={{ position: 'relative', lineHeight: 0 }}>
            {unseenShortlistedCandidatesCount > 0 ? (
              <NavItemBadge>{unseenShortlistedCandidatesCount}</NavItemBadge>
            ) : null}
            <ShortlistedCandidatesIcon />
          </Box>
          Shortlisted Candidates
        </NavLinkItemLabel>
      </NavLinkItem>
    </BottomNavbarRoot>
  );
};
